import { State, Store } from 'easy-peasy';
import { StoreState } from '../store/store';
import { SubtitleTextSizes, SubtitleTextWeights, VideoType } from '../typings';

export namespace ChromecastUtils {
  export const CUSTOM_MESSAGES_CHANNEL = 'urn:x-cast:com.beachbodyondemand.chromecast.messages';

  export enum MessageType {
    SEND_AUTH_TOKEN = 'SEND_AUTH_TOKEN',
    SET_AUTH_TOKEN = 'SET_AUTH_TOKEN',
    SET_USER_GUID = 'SET_USER_GUID',
    SET_LOCALE = 'SET_LOCALE',
    SET_AUDIO_TRACK = 'SET_AUDIO_TRACK',
    SET_SUBTITLE_TRACK = 'SET_SUBTITLE_TRACK',
    SET_MODIFIER_VIDEO = 'SET_MODIFIER_VIDEO',
    SET_MUSIC_VOLUME = 'SET_MUSIC_VOLUME',
    SET_ANY = 'SET_ANY',
    SEND_STATE = 'SEND_STATE',
    STATE = 'STATE',
  }

  export class CustomMessagesHandler {
    store: Store<StoreState>;
    castSession: cast.framework.CastSession;

    constructor(castSession: cast.framework.CastSession, store: Store<StoreState>) {
      this.castSession = castSession;
      this.store = store;
    }

    subscribe = () => {
      this.castSession.addMessageListener(CUSTOM_MESSAGES_CHANNEL, this.handleCustomMessage);
    };

    unsubscribe = () => {
      this.castSession.removeMessageListener(CUSTOM_MESSAGES_CHANNEL, this.handleCustomMessage);
    };

    handleCustomMessage = (channel: string, message: string) => {
      const data = JSON.parse(message);

      if (data.type === MessageType.SEND_AUTH_TOKEN) {
        this.sendAuthToken();
      }
    };

    sendAuthToken = async () => {
      const storeState = this.store.getState();
      const customData = await getCustomData(storeState);
      storeState.chromecast.castSession?.sendMessage(CUSTOM_MESSAGES_CHANNEL, {
        type: MessageType.SET_AUTH_TOKEN,
        authToken: customData.authToken,
      });
    };
  }

  export async function getCustomData(storeState: State<StoreState>) {
    let authToken = '';

    if (storeState.config.getAccessToken) {
      const { accessToken } = await storeState.config.getAccessToken!();
      authToken = accessToken;
    }

    return {
      authToken,
      locale: storeState.config.language,
      audioTrackName: storeState.player.audioTrack?.label,
      subtitleTrackLanguage: storeState.subtitles.activeSubtitle?.language,
      useModifier: storeState.player.videoItem?.type === VideoType.MODIFIER,
      extraHeartbeatParams: storeState.config.extraHeartbeatParams,
      recommendationId: storeState.config.recommendationId,
      entityId: storeState.config.entityId,
    };
  }

  export function getTextTrackStyle(storeState: State<StoreState>) {
    const textTrackStyle = new chrome.cast.media.TextTrackStyle();
    textTrackStyle.backgroundColor = '#000000A6';
    textTrackStyle.foregroundColor = `${storeState.subtitles.subtitleTextColor || '#FFFFFF'}FF`;
    textTrackStyle.fontScale = getFontScale(storeState.subtitles.subtitleTextSize);
    textTrackStyle.fontStyle = getFontStyle(storeState.subtitles.subtitleTextWeight);
    return textTrackStyle;
  }

  function getFontScale(size: SubtitleTextSizes | undefined) {
    switch (size) {
      case SubtitleTextSizes.Smallest:
        return 0.5;
      case SubtitleTextSizes.Small:
        return 0.75;
      case SubtitleTextSizes.Normal:
        return 1.0;
      case SubtitleTextSizes.Large:
        return 1.25;
      case SubtitleTextSizes.Largest:
        return 1.5;
      default:
        return 1.0;
    }
  }

  function getFontStyle(weigth: SubtitleTextWeights | undefined) {
    switch (weigth) {
      case SubtitleTextWeights.Bold:
        return chrome.cast.media.TextTrackFontStyle.BOLD;
      default:
        return chrome.cast.media.TextTrackFontStyle.NORMAL;
    }
  }

  export function currentVideoPlaysRemotely(storeState: State<StoreState>) {
    return storeState.chromecast.castSession;
  }
}
